import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { capitalize } from './searchFormat';
import type { GeographicalResolution, RetrievalMode, SearchResponseData } from './types';

const Area = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="space-y-2 border-t border-gray-100 pt-4 first:border-t-0 first:pt-0">
    <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">{title}</h3>
    <div className="space-y-2">{children}</div>
  </section>
);

const Row = ({ label, value }: { label: string; value: ReactNode }) => {
  if (value === null || value === undefined || value === '') return null;

  return (
    <div className="text-sm text-gray-600">
      <div className="text-xs text-gray-400">{label}</div>
      <div className="break-words">{value}</div>
    </div>
  );
};

const BoolRow = ({
  label,
  value,
  trueLabel = 'Enabled',
  falseLabel = 'Disabled',
}: {
  label: string;
  value: boolean | undefined;
  trueLabel?: string;
  falseLabel?: string;
}) => {
  if (value === undefined) return null;

  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-gray-400">{label}</span>
      <span
        className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
          value ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
        }`}
      >
        {value ? trueLabel : falseLabel}
      </span>
    </div>
  );
};

const ListRow = ({ label, values }: { label: string; values: ReactNode[] }) => (
  <div className="text-sm text-gray-600">
    <div className="text-xs text-gray-400">{label}</div>
    {values.length === 0 ? (
      <div className="text-gray-400">None</div>
    ) : (
      <div className="space-y-0.5">
        {values.map((value, index) => (
          <div key={index} className="break-words">
            {value}
          </div>
        ))}
      </div>
    )}
  </div>
);

/** A ListRow that stays collapsed until opened, for values that can run long. */
const ExpandableListRow = ({ label, values }: { label: string; values: ReactNode[] }) => (
  <details className="group/diaglist text-sm text-gray-600">
    <summary className="flex cursor-pointer select-none list-none items-center justify-between gap-2 text-xs text-gray-400">
      <span>
        {label} ({values.length})
      </span>
      <ChevronDown className="w-3.5 h-3.5 shrink-0 transition-transform group-open/diaglist:rotate-180" />
    </summary>
    {values.length === 0 ? (
      <div className="mt-1 text-gray-400">None</div>
    ) : (
      <div className="mt-1 space-y-0.5">
        {values.map((value, index) => (
          <div key={index} className="break-words">
            {value}
          </div>
        ))}
      </div>
    )}
  </details>
);

/** Nullable counts must render as 0 rather than disappearing. */
function numberValue(value: number | null | undefined): string | null {
  return value === null || value === undefined ? null : String(value);
}

function methodValue(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? capitalize(trimmed) : null;
}

function nonEmpty(values: Array<string | null | undefined>): string[] {
  return values.map((v) => v?.trim()).filter((v): v is string => Boolean(v));
}

const GeographicalResolutionRows = ({ geo }: { geo: GeographicalResolution }) => {
  const parsedAddresses = nonEmpty((geo.parsed_addresses ?? []).map((a) => a.formatted_address));
  const validatedAddresses = nonEmpty((geo.validated_addresses ?? []).map((a) => a.formatted_address));
  const ratingValuations = (geo.rating_valuations ?? []).filter((v) =>
    Boolean(v.valuation_reference_number?.trim())
  );
  const parcels = (geo.parcels ?? []).filter((p) => Boolean(p.parcel_id?.trim()));

  return (
    <>
      {parsedAddresses.length > 0 && <ListRow label="Parsed addresses" values={parsedAddresses} />}
      <Row label="Parsed street" value={geo.parsed_street?.formatted_street} />
      {validatedAddresses.length > 0 && (
        <ListRow label="Validated addresses" values={validatedAddresses} />
      )}
      <Row label="Validated street" value={geo.validated_street?.formatted_street} />
      {ratingValuations.length > 0 && (
        <ListRow
          label="Rating valuations"
          values={ratingValuations.map((valuation) => (
            <>
              {valuation.valuation_reference_number}
              {valuation.assessment_id && (
                <span className="block text-xs text-gray-400">
                  Assessment number {valuation.assessment_id}
                </span>
              )}
            </>
          ))}
        />
      )}
      {parcels.length > 0 && (
        <ListRow
          label="Parcels"
          values={parcels.map((parcel) => (
            <>
              {parcel.parcel_id}
              {parcel.appellation && (
                <span className="block text-xs text-gray-400">{parcel.appellation}</span>
              )}
            </>
          ))}
        />
      )}
    </>
  );
};

const RETRIEVAL_MODE_LABELS: Record<RetrievalMode, string> = {
  semantic_similarity: 'Semantic similarity',
  filter_tags: 'Filter tags',
};

const retrievalModeValue = (mode: RetrievalMode | null | undefined): string | null =>
  mode ? (RETRIEVAL_MODE_LABELS[mode] ?? mode) : null;

const QueryDiagnosticsPanel = ({ data }: { data: SearchResponseData }) => {
  const preRetrieval = data.pre_retrieval;
  const retrieval = data.retrieval;
  const postRetrieval = data.post_retrieval;
  const entityRecognition = preRetrieval?.entity_recognition;
  const geo = preRetrieval?.geographical_resolution;
  const iscFiltering = postRetrieval?.information_security_classification_filtering;
  const dynamicFiltering = postRetrieval?.dynamic_filtering;
  const filterTagsMode = retrieval?.mode === 'filter_tags';

  return (
    <div className="space-y-4">
      {preRetrieval && (
        <Area title="Pre-retrieval">
          <Row label="Normalised query" value={data.normalised_query} />

          <BoolRow label="Spell checking" value={preRetrieval.spell_checking?.enabled} />
          {preRetrieval.spell_checking?.enabled && (
            <Row label="Alternative query" value={preRetrieval.spell_checking.alternative_query} />
          )}

          <BoolRow label="Query enhancement" value={preRetrieval.query_enhancement?.enabled} />
          {preRetrieval.query_enhancement?.enabled && (
            <>
              <Row label="Method" value={methodValue(preRetrieval.query_enhancement.method)} />
              <Row label="Enhanced query" value={preRetrieval.query_enhancement.enhanced_query} />
            </>
          )}

          <BoolRow label="Entity recognition" value={entityRecognition?.enabled} />
          {entityRecognition?.enabled && (
            <>
              <ExpandableListRow
                label="Entities"
                values={(entityRecognition.entities ?? []).map((entity) => (
                  <>
                    {entity.name}
                    <span className="block text-xs text-gray-400">
                      {capitalize(entity.type)} &middot; {capitalize(entity.method)}
                    </span>
                  </>
                ))}
              />
              <Row label="Detected address" value={entityRecognition.detected_address} />
              <Row label="Detected parcel ID" value={entityRecognition.detected_parcel_id} />
              <Row
                label="Detected legal description"
                value={entityRecognition.detected_legal_description}
              />
              <Row
                label="Detected valuation reference number"
                value={entityRecognition.detected_valuation_reference_number}
              />
              <ListRow
                label="Detected email addresses"
                values={nonEmpty(entityRecognition.detected_email_addresses ?? [])}
              />
              <ListRow
                label="Detected Ozone IDs"
                values={nonEmpty(entityRecognition.detected_ozone_ids ?? [])}
              />
            </>
          )}

          {geo && <GeographicalResolutionRows geo={geo} />}

          <ExpandableListRow
            label="Filter search tags"
            values={nonEmpty(preRetrieval.filter_search_tags ?? [])}
          />

          <Row label="Residual query" value={preRetrieval.residual_query?.query} />
          <ExpandableListRow
            label="Removed spans"
            values={nonEmpty(preRetrieval.residual_query?.removed_spans ?? [])}
          />
        </Area>
      )}

      {retrieval && (
        <Area title="Retrieval">
          <Row label="Mode" value={retrievalModeValue(retrieval.mode)} />
          {filterTagsMode ? (
            <Row label="Ordering" value="Title, no ranking" />
          ) : (
            <Row label="Executed query" value={retrieval.executed_query} />
          )}
          <Row label="Results count" value={numberValue(retrieval.results_count)} />
          <Row label="Top K" value={numberValue(retrieval.top_k)} />
        </Area>
      )}

      {postRetrieval && (
        <Area title="Post-retrieval">
          {iscFiltering && (
            <>
              <Row label="User clearance" value={iscFiltering.user_clearance} />
              <Row
                label="Security filtering excluded count"
                value={numberValue(iscFiltering.excluded_count)}
              />
              <Row
                label="Security filtering results count"
                value={numberValue(iscFiltering.results_count)}
              />
            </>
          )}

          <BoolRow label="Reranking" value={postRetrieval.reranking?.enabled} />
          {postRetrieval.reranking?.enabled && !filterTagsMode && (
            <>
              <Row label="Reranking method" value={methodValue(postRetrieval.reranking.method)} />
              <Row label="Reranking model" value={postRetrieval.reranking.model} />
              <Row
                label="Minimum reranking score"
                value={numberValue(postRetrieval.reranking.min_reranking_score)}
              />
              <Row
                label="Reranking excluded count"
                value={numberValue(postRetrieval.reranking.excluded_count)}
              />
              <Row
                label="Reranking results count"
                value={numberValue(postRetrieval.reranking.results_count)}
              />
            </>
          )}

          <BoolRow label="Dynamic filtering" value={dynamicFiltering?.enabled} />
          {dynamicFiltering?.enabled && (
            <>
              <Row label="Dynamic filtering method" value={methodValue(dynamicFiltering.method)} />
              <Row
                label="Dynamic filtering excluded count"
                value={numberValue(dynamicFiltering.excluded_count)}
              />
              <Row
                label="Dynamic filtering results count"
                value={numberValue(dynamicFiltering.results_count)}
              />
            </>
          )}
        </Area>
      )}

      <Area title="Results">
        <Row label="Results count" value={numberValue(data.results_count)} />
      </Area>
    </div>
  );
};

export default QueryDiagnosticsPanel;
